export const VAT_RATE = 0.19

// Fallback PAYG rates (used if API unavailable, e.g. SSR)
export const K8S_PAYG_DEFAULTS = {
  baseFeeCents: 252,
  cpuCoreHourCents: 1.5,
  memoryGibHourCents: 0.5,
  storageGibMonthCents: 8.0,
  egressGibCents: 1.0
} as const

export const S3_DEFAULTS = {
  storageGbMonthCents: 1.5,
  egressGbCents: 0.5
} as const

// Net metered registry rates, billed like object storage (registry/billing.go)
export const REGISTRY_DEFAULTS = {
  storageGbMonthCents: 1.5,
  egressGbCents: 0.5
} as const

// Fixed products (net prices in EUR from seed-products.sql / pricing.md)
export const K8S_NAMESPACE_TIERS = [
  { id: 'ns-s', name: 'S', cpu: '1 vCPU', ram: '2 GiB', priceNet: 12.61, egressGiB: 100 },
  { id: 'ns-m', name: 'M', cpu: '2 vCPU', ram: '4 GiB', priceNet: 24.37, egressGiB: 250 },
  { id: 'ns-l', name: 'L', cpu: '4 vCPU', ram: '8 GiB', priceNet: 41.18, egressGiB: 500 }
] as const

export const K8S_CLUSTER_TYPES = [
  { id: 'hybrid', name: 'Hybrid Dedicated', priceNet: 335.29, workerPriceNet: 100.0, hasWorkers: true },
  { id: 'sovereign', name: 'Sovereign / Compliance', priceNet: 629.41, workerPriceNet: 0, hasWorkers: false }
] as const

export const K8S_ADDONS = [
  { id: 'rwx-storage', name: 'RWX Storage 50 GiB', priceNet: 15.97, hasQuantity: false },
  { id: 'managed-db', name: 'Managed Database', priceNet: 24.37, hasQuantity: false },
  { id: 'extended-backup', name: 'Extended Backup', priceNet: 2.10, hasQuantity: true, quantityUnit: '10 GiB', quantityMin: 1, quantityMax: 50 }
] as const

// Webhosting tiers — gross prices to match product pages
export const WEBHOSTING_TIERS = [
  { id: 'nano', name: 'Nano', storage: '50 GB', priceGross: 3.49 },
  { id: 'micro', name: 'Micro', storage: '100 GB', priceGross: 6.99 },
  { id: 'mega', name: 'Mega', storage: '200 GB', priceGross: 14.99 },
  { id: 'giga', name: 'Giga', storage: '500 GB', priceGross: 36.99 },
  { id: 'tera', name: 'Tera', storage: '1 TB', priceGross: 74.99 },
  { id: 'peta', name: 'Peta', storage: '2 TB', priceGross: 144.99 }
] as const

// VServer tiers — gross prices to match product pages (single source of truth,
// also consumed by the VServer landing page's fallback tiers)
export const VSERVER_TIERS = [
  { slug: 'vserver-s', name: 'S', vcpu: 2, ramGiB: 4, diskGB: 60, snapshots: 2, priceGross: 7.99, popular: false },
  { slug: 'vserver-m', name: 'M', vcpu: 4, ramGiB: 8, diskGB: 120, snapshots: 3, priceGross: 14.99, popular: true },
  { slug: 'vserver-l', name: 'L', vcpu: 8, ramGiB: 16, diskGB: 240, snapshots: 3, priceGross: 29.99, popular: false },
  { slug: 'vserver-xl', name: 'XL', vcpu: 12, ramGiB: 32, diskGB: 360, snapshots: 5, priceGross: 54.99, popular: false }
] as const

// E-Mail Hosting — fallback values (canonical source: mail-domain product features)
export const MAIL_DEFAULTS = {
  baseFeeGrossCents: 199,
  annualFeeGrossCents: 1990,
  includedStorageGB: 10,
  includedOutbound: 1000,
  storageOverageGbCentsNet: 10.0,
  outboundOveragePer1000CentsNet: 150.0
} as const

export interface CalculatorPreset {
  id: string
  nameKey: string
  descKey: string
  icon: string
  categories: string[]
  values: {
    k8sPayg?: { cpuCores: number; cpuHours: number; ramGib: number; ramHours: number; storageGib: number; egressGib: number }
    s3?: { storageGb: number; egressGb: number }
    k8sNamespace?: { tier: string; quantity: number }
    k8sAddon?: { selected: Record<string, boolean> }
  }
}

export const CALCULATOR_PRESETS: CalculatorPreset[] = [
  {
    id: 'small-app',
    nameKey: 'pricingCalculator.presets.smallApp',
    descKey: 'pricingCalculator.presets.smallAppDesc',
    icon: 'mdi-application-outline',
    categories: ['k8sPayg', 's3'],
    values: {
      k8sPayg: { cpuCores: 1, cpuHours: 730, ramGib: 2, ramHours: 730, storageGib: 10, egressGib: 20 },
      s3: { storageGb: 50, egressGb: 20 }
    }
  },
  {
    id: 'web-platform',
    nameKey: 'pricingCalculator.presets.webPlatform',
    descKey: 'pricingCalculator.presets.webPlatformDesc',
    icon: 'mdi-web',
    categories: ['k8sPayg', 's3'],
    values: {
      k8sPayg: { cpuCores: 4, cpuHours: 730, ramGib: 8, ramHours: 730, storageGib: 50, egressGib: 100 },
      s3: { storageGb: 200, egressGb: 100 }
    }
  },
  {
    id: 'team-namespace',
    nameKey: 'pricingCalculator.presets.teamNamespace',
    descKey: 'pricingCalculator.presets.teamNamespaceDesc',
    icon: 'mdi-account-group-outline',
    categories: ['k8sNamespace', 'k8sAddon'],
    values: {
      k8sNamespace: { tier: 'ns-m', quantity: 1 },
      k8sAddon: { selected: { 'managed-db': true } }
    }
  }
]
